/**
 * Today's check-in, the desk set-up flow and the header's Check out button
 * (design: weco2/homework-desk.jsx `useDesk`), backed by services/office
 * `/me/workday/` rather than the mock's localStorage store.
 *
 * One query key for every reader — the HomeWork gate, the module header, My day
 * and the suite header's Check out button — so a check-out in one place is
 * seen by all of them at once.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { officeService } from "../api";
import type { Workday } from "../api/types";

export const WORKDAY_KEY = ["office", "workday"] as const;

export function useWorkday(enabled = true) {
  return useQuery({
    queryKey: WORKDAY_KEY,
    queryFn: () => officeService.getWorkday(),
    enabled,
    staleTime: 30_000,
    // A check-out made in another tab, or the day rolling over, should land
    // when the person comes back to this one.
    refetchOnWindowFocus: true,
    retry: 1,
  });
}

/** Everything a check-in or check-out changes, besides the workday itself. */
function invalidateAfterWorkday(qc: ReturnType<typeof useQueryClient>) {
  for (const key of [
    ["office", "me"],
    ["office", "timer"],
    ["office", "presence"],
    ["office", "floors"],
    ["office", "my-desks"],
  ]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

export function useCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: { finishOnboarding?: boolean } = {}) =>
      officeService.checkIn(opts.finishOnboarding ? { finish_onboarding: true } : {}),
    onSuccess: (workday) => {
      qc.setQueryData(WORKDAY_KEY, workday);
      invalidateAfterWorkday(qc);
    },
  });
}

export function useCheckOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => officeService.checkOut(),
    onSuccess: (workday) => {
      qc.setQueryData(WORKDAY_KEY, workday);
      invalidateAfterWorkday(qc);
    },
  });
}

export function useFloorDesks(enabled = true) {
  return useQuery({
    queryKey: ["office", "my-desks"],
    queryFn: () => officeService.getMyFloorDesks(),
    enabled,
    staleTime: 15_000,
  });
}

export function useClaimDesk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deskId: string) => officeService.claimDesk(deskId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WORKDAY_KEY });
      void qc.invalidateQueries({ queryKey: ["office", "my-desks"] });
      void qc.invalidateQueries({ queryKey: ["office", "desks"] });
      void qc.invalidateQueries({ queryKey: ["office", "map"] });
    },
  });
}

export function useJoinDeskWaitlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => officeService.joinDeskWaitlist(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: WORKDAY_KEY }),
  });
}

/**
 * Seconds at the desk today, advancing locally while checked in.
 *
 * The server's figure is exact as of its `as_of`; between fetches it is moved
 * on by the wall clock every 30 seconds, the design's own cadence.
 */
export function useAtDeskSeconds(workday: Workday | undefined | null): number {
  const [, tick] = useState(0);
  const checkedIn = workday?.status === "in";
  useEffect(() => {
    if (!checkedIn) return;
    const id = window.setInterval(() => tick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [checkedIn]);
  if (!workday) return 0;
  if (!checkedIn) return workday.at_desk_seconds;
  const since = Date.parse(workday.as_of);
  const extra = Number.isFinite(since) ? Math.max(0, (Date.now() - since) / 1000) : 0;
  return workday.at_desk_seconds + extra;
}
